import Image from 'next/image';
import {
  Box,
  Center,
  Flex,
  Heading,
  ListItem,
  SimpleGrid,
  Text,
  UnorderedList,
} from '@chakra-ui/react';

import Link from 'components/general/Link';
import Panel from '@inspire/ui/general/Panel';
import Page from '@inspire/ui/chakra/layout/Page';
import KitPanel from 'components/general/KitPanel';
import Layout from 'components/layout/Layout';

import { cdnURL } from '@inspire/util/cdn';
import { useMyProfile } from 'lib/hooks';

const PrintempsOrientationPage = () => {
  useMyProfile(); // to trigger lastDayOfActivity update

  return (
    <Page title="Le Printemps de l'orientation" headingIsTitle={false}>
      <Panel>
        <Box>
          <Heading size="lg" textAlign={'center'}>
            INSPIRE vous accompagne pendant le Printemps de l’Orientation
          </Heading>
          <SimpleGrid columns={2} spacing={10}>
            <Center>
              <Image
                src={`/img/printemps/logo.png`}
                alt="logo Printemps Orientation"
                width="320px"
                height="164px"
              />
            </Center>
            <Center>
              <Image
                src={cdnURL('/6093-onisep.jpg')}
                alt="logo Onisep"
                width="320px"
                height="167px"
              />
            </Center>
          </SimpleGrid>
          <SimpleGrid columns={[1, 2, 4]} spacing={0} mt={8}>
            <Center>
              <Link href="#lyceens">
                <Box textAlign={'center'}>
                  <Heading size="xs">Actions pour les lycéens</Heading>
                  <Image
                    src="/img/printemps/actions-lyceens.png"
                    alt="actions pour les lycéens"
                    width="160px"
                    height="160px"
                  />
                </Box>
              </Link>
            </Center>
            <Box>
              <Link href="#equipes-pedagogiques">
                <Box textAlign={'center'}>
                  <Heading size="xs">
                    Actions pour les équipes pédagogiques
                  </Heading>
                  <Image
                    src={'/img/printemps/actions-equipes-peda.png'}
                    alt="actions pour les équipes pédagogiques"
                    width="160px"
                    height="160px"
                  />
                </Box>
              </Link>
            </Box>
            <Box>
              <Link href="#parents" className="po-anchor">
                <Box textAlign={'center'}>
                  <Heading size="xs">Actions pour les parents</Heading>
                  <Image
                    src={'/img/printemps/actions-parents.png'}
                    alt="actions pour les parents"
                    width="160px"
                    height="160px"
                  />
                </Box>
              </Link>
            </Box>
            <Box>
              <Link href="#presse">
                <Box textAlign={'center'}>
                  <Heading size="xs">Relations presse</Heading>
                  <Image
                    src={`/img/printemps/actions-presse.png`}
                    alt="actions pour la presse"
                    width="160px"
                    height="160px"
                  />
                </Box>
              </Link>
            </Box>
          </SimpleGrid>
        </Box>
      </Panel>

      <Box mt={8}>
        <KitPanel title="Actions pour les lycéens" id="lyceens">
          <Heading size="sm">
            Pour tous : témoignages d’étudiants à distance
          </Heading>
          <Flex flexDir={['column', null, 'row']}>
            <Box flex={3}>
              <Text>
                <strong>Lors de lives sur Instagram</strong> <br></br>
                Avec l’intervention d’étudiants pour partager leurs expériences
                et répondre aux questions des lycéens en direct{' '}
                <i>(puis rediffusion sur Youtube) :</i>
              </Text>

              <UnorderedList>
                <ListItem>
                  <strong>
                    {' '}
                    Mardi 21 mars → 18h - 18h45 : <br></br>{' '}
                  </strong>
                  <Link
                    target="_blank"
                    href="https://www.instagram.com/article_un/?hl=fr"
                  >
                    <i>
                      {' '}
                      {'>'} Poursuivre ses études après un bac pro : des
                      étudiants témoignent{' '}
                    </i>
                  </Link>
                </ListItem>

                <ListItem>
                  <strong>
                    {' '}
                    Mercredi 22 mars → 14h - 14h45 : <br></br>{' '}
                  </strong>
                  <Link
                    target="_blank"
                    href="https://www.instagram.com/article_un/?hl=fr"
                  >
                    <i>
                      {' '}
                      {'>'} Financer ses études : des étudiants témoignent{' '}
                    </i>
                  </Link>
                </ListItem>

                <ListItem>
                  <strong>
                    {' '}
                    Mercredi 22 mars → 17h - 17h45 : <br></br>{' '}
                  </strong>
                  <Link
                    target="_blank"
                    href="https://www.instagram.com/article_un/?hl=fr"
                  >
                    <i>
                      {' '}
                      {'>'} Étudier loin de chez soi : des étudiants témoignent{' '}
                    </i>
                  </Link>
                </ListItem>
              </UnorderedList>

              <Text>
                <strong>
                  → Lien vers le compte Youtube d’Article 1 pour les
                  rediffusions :
                </strong>{' '}
                <br></br>
                <Link target="_blank" href="https://www.youtube.com/c/Article1">
                  https://www.youtube.com/c/Article1
                </Link>
              </Text>
            </Box>

            <Box flex={1} textAlign="center" alignSelf={'center'}>
              <Image
                src={`/img/printemps/network.png`}
                className="full-margin"
                alt="Webconferences"
                height="160px"
                width="160px"
              />
            </Box>
          </Flex>
        </KitPanel>
      </Box>

      <Box mt={8}>
        <KitPanel
          title="Actions pour les équipes pédagogiques"
          id="equipes-pedagogiques"
        >
          <Heading size="sm">
            Présentation et mise à disposition d’outils
          </Heading>
          <Flex flexDir={['column', null, 'row']}>
            <Box flex={3}>
              <UnorderedList mt={0}>
                <ListItem>
                  <strong>
                    Présentation en ligne de la plateforme INSPIRE{' '}
                  </strong>{' '}
                  : pour permettre une meilleure prise en main et un
                  accompagnement de sa classe (rediffusion sur{' '}
                  <Link
                    href="https://www.youtube.com/c/Article1"
                    target="_blank"
                  >
                    Youtube
                  </Link>
                  ) <br></br>
                  <strong>→ Mardi 21 mars à 11h30</strong> <br></br>
                  <strong>
                    → Présentation équipes pédagogiques : Se projeter dans le
                    supérieur avec la plateforme INSPIRE
                  </strong>{' '}
                  <br></br>
                  <strong>
                    →{' '}
                    <Link
                      href="https://forms.gle/VmwvVH3A8tK58GGw6"
                      target="_blank"
                    >
                      S'inscrire ici
                    </Link>
                  </strong>{' '}
                  <br></br>
                </ListItem>

                <ListItem>
                  <strong>Mise à disposition de fiches activités</strong>{' '}
                  utilisables en classe sur la thématique des études supérieures
                  et de la vie étudiante. <br></br>→ Lien de téléchargement :{' '}
                  <Link href="https://bit.ly/kitprofs23" target="_blank">
                    https://bit.ly/kitprofs23
                  </Link>
                </ListItem>
              </UnorderedList>
            </Box>
            <Box flex={1} textAlign="center" alignSelf={'center'}>
              <Image
                src={`/img/printemps/ateliers.png`}
                alt="Ateliers"
                width="160px"
                height="160px"
              />
            </Box>
          </Flex>
          {/* <Box>
            <Heading size="sm">Nos actions régionales :</Heading>
            <Text>
              Dans chaque académie partenaire, nous proposons une présentation
              de la plateforme INSPIRE et de nos outils à destination des
              équipes pédagogiques pour accompagner les élèves de Première et de
              Terminale dans leurs choix d’orientation.
            </Text>
          </Box> */}
        </KitPanel>
      </Box>

      <Box mt={8}>
        <KitPanel id="parents" title="Actions pour les parents">
          <Flex flexDir={['column', null, 'row']}>
            <Box flex={3}>
              <UnorderedList mt={0}>
                <ListItem>
                  <strong>
                    Présentation en ligne de la plateforme INSPIRE :
                  </strong>{' '}
                  <i>
                    Identification de pistes d’études, conseils pour contacter
                    des étudiants bénévoles, informations sur le financement et
                    la vie étudiante, veille sur les différentes évolutions de
                    filières, etc.(rediffusion sur{' '}
                    <Link
                      href="https://www.youtube.com/c/Article1"
                      target="_blank"
                    >
                      Youtube
                    </Link>
                    ){' '}
                  </i>
                  <br></br>
                  <strong>→ Lundi 20 mars à 18h</strong> <br></br>
                  <strong>
                    → Atelier parents : Se projeter dans le supérieur avec la
                    plateforme INSPIRE
                  </strong>
                  <br></br>
                  <strong>
                    →{' '}
                    <Link
                      href="https://docs.google.com/forms/d/e/1FAIpQLSejjM3OaK1MYAz1PJJd5WNHWBgWTh3hWxJ-9nLCcTuMhW1JCQ/viewform?usp=sf_link"
                      target="_blank"
                    >
                      S'inscrire ici
                    </Link>
                  </strong>{' '}
                  <br></br>
                </ListItem>

                <ListItem>
                  <strong>
                    Mise à disposition d’un fascicule de présentation
                  </strong>{' '}
                  de cette plateforme et de ses ressources pour les parents
                  d’élèves. <br></br>→ Lien de téléchargement :{' '}
                  <Link href="https://bit.ly/kitparents23" target="_blank">
                    https://bit.ly/kitparents23
                  </Link>
                </ListItem>
              </UnorderedList>
            </Box>
            <Box flex={1} alignSelf="center" textAlign={'center'}>
              <Image
                src={`/img/printemps/inspire.png`}
                alt="Ateliers"
                height="160px"
                width="160px"
              />
            </Box>
          </Flex>
        </KitPanel>
      </Box>

      <Box>
        <KitPanel id="presse" title="Relations Presse" mt={8}>
          <Heading size="sm">
            L'opération Printemps de l'Orientation en détails
          </Heading>
          <Text>
            Pour cette troisième édition, l’Éducation nationale et l’Onisep
            s’appuient sur quelques acteurs dont Article 1 pour faire vivre le
            Printemps de l’Orientation. L’idée est ainsi de proposer aux lycéens
            des ressources pour affiner leur projet d’orientation post-bac. Les
            équipes INSPIRE se mobilisent du 20 au 22 mars 2023, pour organiser
            des lives et des ateliers, en ligne et dans les lycées, ainsi que
            des témoignages d’étudiants sur les réseaux sociaux.
          </Text>

          <Text>
            <strong>
              Si vous êtes journaliste et avez une question sur le Printemps de
              l’Orientation ou souhaitez assister à l’une de nos webconférences
              ou de nos ateliers, vous pouvez contacter :
            </strong>
          </Text>
          <Text textAlign={'center'}>
            Nina Djitli Chaumont (Agence Shadow communication) <br></br>
            <Link href="mailto:ninadjitlichaumont@shadowcommunication.fr">
              ninadjitlichaumont@shadowcommunication.fr
            </Link>
            <br></br>
            06 62 94 86 84 <br></br>
          </Text>
        </KitPanel>
      </Box>
    </Page>
  );
};

PrintempsOrientationPage.getWrappers = (page) => (
  <Layout variant="adaptative" rowVariant="gray" containerWidth="lg">
    {page}
  </Layout>
);

export default PrintempsOrientationPage;
